import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from './cn'
import { paginationRange, paginationSummary } from './pagination-utils'
import { t } from '@/lib/i18n'

export interface PaginationProps {
  /** Current page, 1 based (URL param `p`). */
  page: number
  /** Total number of items, used for the page count and the "1 to 30 of 2,314" summary. */
  total: number
  pageSize: number
  onPageChange: (page: number) => void
  /** Override the computed page count. */
  pageCount?: number
  hideSummary?: boolean
  className?: string
}

const pageBtn =
  'inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-meta font-semibold tabular-nums ' +
  'transition-colors duration-150 ease-out focus-ring'

/** Numbered pagination with Previous and Next, ellipses and an item summary. */
export function Pagination({ page, total, pageSize, onPageChange, pageCount, hideSummary, className }: PaginationProps) {
  const pages = pageCount ?? Math.max(1, Math.ceil(total / Math.max(1, pageSize)))
  const current = Math.min(Math.max(1, page), pages)
  const summary = paginationSummary(current, pageSize, total)
  if (pages <= 1 && (hideSummary || !summary)) return null
  const go = (p: number) => {
    if (p >= 1 && p <= pages && p !== current) onPageChange(p)
  }
  const navBtn =
    'inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-meta font-semibold text-text transition-colors duration-150 ' +
    'ease-out hover:bg-surface-2 focus-ring disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-4'
  return (
    <nav aria-label={t('Pagination')} className={cn('flex flex-wrap items-center justify-between gap-x-4 gap-y-2', className)}>
      {pages > 1 ? (
        <div className="flex items-center gap-1">
          <button type="button" className={navBtn} onClick={() => go(current - 1)} disabled={current <= 1}>
            <ChevronLeft strokeWidth={1.75} aria-hidden="true" />
            <span className="max-sm:sr-only">{t('Previous')}</span>
          </button>
          <ul className="flex items-center gap-1">
            {paginationRange(current, pages).map((item) =>
              typeof item === 'number' ? (
                <li key={item}>
                  <button
                    type="button"
                    onClick={() => go(item)}
                    aria-label={t('Page {page}', { page: item })}
                    aria-current={item === current ? 'page' : undefined}
                    className={cn(
                      pageBtn,
                      item === current ? 'bg-primary text-on-primary' : 'text-muted hover:bg-surface-2 hover:text-text',
                    )}
                  >
                    {item}
                  </button>
                </li>
              ) : (
                <li key={item} aria-hidden="true" className="inline-flex h-8 w-6 items-center justify-center text-meta text-muted">
                  …
                </li>
              ),
            )}
          </ul>
          <button type="button" className={navBtn} onClick={() => go(current + 1)} disabled={current >= pages}>
            <span className="max-sm:sr-only">{t('Next')}</span>
            <ChevronRight strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
      ) : (
        <span />
      )}
      {!hideSummary && summary && <p className="text-meta text-muted tabular-nums">{summary}</p>}
    </nav>
  )
}
